/**
 * React Query hooks for training blocks.
 *
 * Backed by the /blocks API surface (phase 1 + 2). Phase 3 is
 * server-side only — the SPA doesn't need to know that activations
 * trigger backfill, the activated detail just comes back with days
 * already populated.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosClient } from '../lib/api.ts';
import type {
  ActivateBlockRequest,
  BlockStats,
  BlockStatus,
  BlockSummaryResponse,
  DeepDiveResponse,
  TrainingBlockDetail,
  TrainingBlocksListResponse,
} from '../types/api.ts';

const STALE_MS = 30_000;

export function useBlocks(status: BlockStatus | 'all' = 'all') {
  return useQuery<TrainingBlocksListResponse>({
    queryKey: ['blocks', { status }],
    queryFn: () =>
      axiosClient.get('/blocks', { params: { status } }).then((r) => r.data),
    staleTime: STALE_MS,
  });
}

export function useBlock(id: number | null) {
  return useQuery<TrainingBlockDetail>({
    queryKey: ['blocks', id],
    queryFn: () => axiosClient.get(`/blocks/${id}`).then((r) => r.data),
    enabled: id !== null && Number.isFinite(id),
    staleTime: STALE_MS,
  });
}

export function useImportBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      payload,
      replaceBlockId,
    }: {
      payload: object;
      replaceBlockId?: number;
    }) =>
      axiosClient
        .post('/blocks/import', payload, {
          params: replaceBlockId ? { replace_block_id: replaceBlockId } : {},
        })
        .then((r) => r.data as TrainingBlockDetail),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocks'] });
    },
  });
}

export function useActivateBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body?: ActivateBlockRequest;
    }) =>
      axiosClient
        .post(`/blocks/${id}/activate`, body ?? {})
        .then((r) => r.data as TrainingBlockDetail),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: ['blocks', variables.id] });
    },
  });
}

export function useEndBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      axiosClient.post(`/blocks/${id}/end`).then((r) => r.data as TrainingBlockDetail),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['blocks'] });
      qc.invalidateQueries({ queryKey: ['blocks', id] });
    },
  });
}

export function useDeleteBlock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => axiosClient.delete(`/blocks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['blocks'] });
    },
  });
}

export function useBlockStats(id: number | null) {
  return useQuery<BlockStats>({
    queryKey: ['blocks', id, 'stats'],
    queryFn: () =>
      axiosClient.get(`/blocks/${id}/stats`).then((r) => r.data),
    enabled: id !== null && Number.isFinite(id),
    staleTime: STALE_MS,
  });
}

export function useGenerateBlockSummary() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (blockId: number) =>
      axiosClient
        .post(`/blocks/${blockId}/summary`)
        .then((r) => r.data as BlockSummaryResponse),
    onSuccess: (_data, blockId) => {
      qc.invalidateQueries({ queryKey: ['blocks', blockId] });
    },
  });
}

export function useAnalyzeDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      blockId,
      dayId,
    }: {
      blockId: number;
      dayId: number;
    }) =>
      axiosClient
        .post(`/blocks/${blockId}/days/${dayId}/analyze`)
        .then((r) => r.data as DeepDiveResponse),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['blocks', variables.blockId] });
    },
  });
}
